import { shallowRef, provide, inject } from 'vue'

/**
 * Registre de sections repliables pour une page de détail.
 *
 * Chaque `CollapsibleSection` présent dans l'arbre s'auto-enregistre (titre,
 * icône, élément DOM + contrôles expand/collapse). Le composant `SectionTree`
 * consomme ce registre pour afficher un sommaire cliquable et piloter
 * l'ouverture/fermeture des sections — sans que la page parente n'ait à
 * connaître ses sections à l'avance.
 *
 * L'ordre exposé suit toujours l'ordre du DOM (utile car certaines sections
 * apparaissent/disparaissent via v-if selon le type de client).
 */
const SECTION_TREE_KEY = Symbol('sectionTree')

export function provideSectionTree() {
  // shallowRef : on garde les refs `collapsed` d'origine intactes (pas de
  // conversion profonde qui casserait la réactivité lue depuis le sommaire).
  const sections = shallowRef([])
  // Compteur propre à cette page (provideSectionTree est appelé une fois par
  // page) : garantit des ids uniques par section, indispensable aux :key Vue.
  let idCounter = 0

  const sortByDom = () => {
    const arr = [...sections.value]
    arr.sort((a, b) => {
      if (!a.el || !b.el || a.el === b.el) return 0
      const pos = a.el.compareDocumentPosition(b.el)
      if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1
      if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1
      return 0
    })
    sections.value = arr
  }

  const register = (entry) => {
    // Le registre attribue lui-même l'id (le compteur vit ici, pas dans le
    // <script setup> des sections qui, lui, s'exécute une fois par instance).
    entry.id = `section-${++idCounter}`
    sections.value = [...sections.value, entry]
    sortByDom()
    return () => unregister(entry.id)
  }

  const unregister = (id) => {
    sections.value = sections.value.filter((s) => s.id !== id)
  }

  const expandAll = () => sections.value.forEach((s) => s.expand?.())
  const collapseAll = () => sections.value.forEach((s) => s.collapse?.())

  const registry = { sections, register, unregister, expandAll, collapseAll, sortByDom }
  provide(SECTION_TREE_KEY, registry)
  return registry
}

/**
 * Injecté par `CollapsibleSection`. Renvoie `null` si la section n'est pas
 * rendue dans une page qui fournit un registre (le composant reste autonome).
 */
export function useSectionTreeRegistry() {
  return inject(SECTION_TREE_KEY, null)
}
